// AccessWizard

import {Dispatch, SetStateAction} from 'react'

export enum CASBServiceNameEnum {
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
}

export enum CASB_ACCESS_STATUS {
  GRANT_ACCESS = 'GRANT_ACCESS',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  ACCESS_DENIED = 'ACCESS_DENIED',
  FAILURE = 'FAILURE',
}

export interface AppsTypes {
  authorized: string[]
  unauthorized: string[]
  reauthorized: string[]
  comingSoon: string[]
}

// Applications

export interface CASBApplicationNode {
  authorization: CASBAuthorizationNode
}
export interface CASBAuthorizationNode {
  link?: string
  status: string
}
export interface CASBApplications {
  [key: string]: CASBApplicationNode
}
export interface CASBApplicationsResponse {
  applications: CASBApplications
}
export interface GoogleAdminConfirmRequest {
  adminEmail: string
  state: string
  platform: string
}

export interface CASBApplicationsData {
  data: CASBApplicationsResponse
}

export interface ConfirmEmailForm {
  email: string
}

export interface RichTextComponent {
  type: string
  text?: string
  class: string
  link?: string
  texts?: string[]
  dataCy?: string
}

export interface WizardModelOneProps {
  link?: string
  isLoading?: boolean
  name: string
  btnName?: string
  icon: string
}

export interface WizardModelTwoProps {
  name: string
  app: string
  scopes: string[]
  clientId: string
  appState?: string
  steps: number
  icon: string
}

export interface AdminConsent
  extends WizardModelOneProps,
    WizardModelTwoProps {}

export interface CASBAdminConsentResponse {
  adminConsent: AdminConsent
  appName: string
}

export interface CASBAdminConsentData {
  data: CASBAdminConsentResponse
}

// Overview

export type CASBClassificationOptionsType =
  | 'ip'
  | 'pii'
  | 'phi'
  | 'pci'
  | 'other'
  | 'EXTERNAL'

export interface TopClassificationType {
  name: ShareStatusEnum.EXTERNAL | CASBClassificationOptionsType
  count: number
  linkTo: LinkTo
  numOfReviews: number
  weight: number
}

export type ShareStatus = 'PUBLIC' | 'PRIVATE' | 'EXTERNAL'

export enum ReviewStatusEnum {
  REVIEWED = 'REVIEWED',
  UNREVIEWED = 'REVIEW',
}

export enum ShareStatusEnum {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
  EXTERNAL = 'EXTERNAL',
}

export enum CASBDriveTypeEnum {
  PERSONAL = 'personal',
  SHARED = 'shared',
  BUSINESS = 'business',
  DOCUMENT_LIBRARY = 'documentlibrary',
}

export enum CASBClassificationOptionsEnum {
  IP = 'ip',
  PHI = 'phi',
  PCI = 'pci',
  PII = 'pii',
  OTHER = 'other',
}

export type CASBDetectionChipKey =
  | 'PHI'
  | 'PII'
  | 'IP'
  | 'PCI'
  | 'OTHER'
  | 'UNCLASSIFIED'

export interface DetectionChipTheme {
  background: string
  border: string
}

export type DetectionChipThemeMap = Record<
  CASBDetectionChipKey,
  DetectionChipTheme
>

export type StringToNumberRecord = Record<string, number>

export type CASBFileSummaryType = {
  numOfFiles: number
  lastUpdated: string
}

export type CASBClassificationsReviewSummary = {
  numOfReviews: number
  lastReviewed: string
}

export type CASBNumberOfFilesType = {
  total: number
  public: number
  private: number
}

export interface CreateClassificationType
  extends Partial<CASBFileSummaryType>,
    Partial<CASBClassificationsReviewSummary> {
  classificationType: CASBClassificationOptionsType
}

export interface CASBClassificationType {
  classificationType: CASBClassificationOptionsType
  fileSummary: CASBFileSummaryType
  reviewSummary?: CASBClassificationsReviewSummary
}

export interface CASBExternalFiles {
  numOfFiles: number
  numOfReviews: number
  lastUpdated: string
  lastReviewed: string
}

export interface CASBOverviewResponse {
  appName: string
  classifications: CASBClassificationType[]
  externalFiles: CASBExternalFiles
  initialScanInProgress?: boolean
  numOfFiles: CASBNumberOfFilesType
}

export interface CASBLHSPanelOptionProps {
  name: string
}

export interface OnboardingAppNamesType {
  [key: string]: {name: string; description: string}
}

export interface ClassificationBoxType {
  name: string
  pendingReviewCount?: number
  totalFilesCount?: number
  areAllFilesReviewed?: boolean
  classificationType: string
  hasDetectedFiles?: boolean
}

// Files

export interface FileSummaryResponse {
  createdBy: string
  createdOn: string
  name: string
  uniqueId: string
  comment: string
  shareStatus: ShareStatus
}

export interface CASBFileResponse {
  detectionSummary: {
    classifications: {
      classificationType: CASBClassificationOptionsType
    }[]
  }
  fileSummary: FileSummaryResponse
  reviewSummary: ReviewSummary
  unshareError?: string
}

export interface TransformedFileList extends FileSummaryResponse {
  isReviewed: boolean
  type: CASBClassificationOptionsType[]
  createdOnTimeAgo: string
  unshareError?: string
}

export interface CASBPlatformType {
  platform: string
  filters?: CASBFilesAPIParams
}

export interface CASBReviewFilesRequest {
  platform: string
  id: string
}

export interface CASBRHSPanelProps {
  platform: string
  selectedFile: CASBFileInfo | undefined
  selectedFiles: Map<string, CASBFileInfo>
  updateClassificationsOnFilters: (
    classification: CASBClassificationOptionsType,
  ) => void
  areAllFilesOnDisplaySelected: boolean
  publicAndExternalFilesCount: number
  filters: CASBFilesAPIParams
  hasNextPage: boolean
  handleResetSelectedFiles: () => void
  setSelectedFiles: Dispatch<SetStateAction<Map<string, CASBFileInfo>>>
  searchInputValue?: string
  setIsDomainSearchActive: Dispatch<SetStateAction<boolean>>
  setSearchInputValue: Dispatch<SetStateAction<string>>
  setIsSearchVisible: Dispatch<SetStateAction<boolean>>
  setFilters: Dispatch<SetStateAction<CASBFilesAPIParams>>
  isLoadingFilesData: boolean
  hasFilesOnTable: boolean
}

export interface CASBMultiSelectProps {
  fileName?: string
  selectedFilesTypes: string[]
}

export interface DetectionExcerpt {
  types: string[]
  excerpt: string
}

export interface CASBReviewFileInfoState {
  fileName: string
  detectionChips: string[]
  detectionExcerpts: DetectionExcerpt[]
  excerptSummary: string
  isReviewed: boolean
  isPublic: boolean
  sharedWith: string[]
  shareDetails: ShareDetails
  owner: string
  reviewedOn?: string
  reviewComment?: string
  reviewerEmail?: string
}

export interface CASBReviewFileInfoResponse {
  data: CASBReviewFileInfo
}

export interface CASBReviewFileInfo {
  appName: string
  fileSummary: FileSummary
  detectionSummary: DetectionSummary
  reviewSummary: ReviewSummary
}

export interface DetectionSummary {
  classifications: FileClassification[]
  excerptSummary: string
}

export interface FileClassification {
  classificationType: string
  excerpts: string[]
}

export interface FileSummary {
  name: string
  uniqueId: string
  createdBy: string
  createdOn: Date
  sharedWith: string[]
  shareDetails: ShareDetails
}

export interface ReviewSummary {
  isReviewed: boolean
  reviewedOn?: string
  comment?: string
  reviewer?: string
}

export interface DetectionChipsType {
  chips: string[] | undefined
  chipClasses?: string
  isPrivate?: boolean
  isReviewed?: boolean
}

export interface DetectionsProps extends DetectionChipsType {
  detections: DetectionExcerpt[] | undefined
  isLoading: boolean
}

export interface CASBFileInfo {
  name: string
  uniqueId: string
  createdBy: string
  createdOn: string
  type: string[]
  createdOnTimeAgo: string
  unshareError?: string
  isReviewed: boolean
  comment?: string
  reviewedOn?: string
  reviewerEmail?: string
  shareStatus: ShareStatus
}

export interface CommentsProps {
  canUnshare?: boolean
  selectedFile?: CASBFileInfo
  selectedFiles: Map<string, CASBFileInfo>
  platform: string
  searchInputValue?: string
  filters?: CASBFilesAPIParams
  rhsPanelWidth?: number
  setSelectedFiles: Dispatch<SetStateAction<Map<string, CASBFileInfo>>>
  fileToDisplay?: CASBFileInfo
  isReviewed: boolean
  reviewedOn?: string
  reviewComment?: string
  reviewerEmail?: string
  hasExternalShares: boolean
  hasFiltersApplied: boolean
}

export interface CommentWrapperProps {
  rhsPanelWidth?: number
}

export interface ShareDetails {
  private: string[]
  external: string[]
  public: boolean
}

export interface AdditionalInfoProps {
  maxAdditionalInfoHeight: number
  excerptSummary?: string
  selectedFile?: CASBFileInfo
  isPublic?: boolean
  isReviewed: boolean
  owner?: string
  isFilePrivate?: boolean
  isUnshared?: boolean
  liveFileInfo?: CASBFilesLiveInfoResponse
  isExternal?: boolean
  isSelectedFileDataLoading: boolean
  shareDetails?: ShareDetails
  onDomainSearch?: (domain: string) => void
  isFilesDataLoading: boolean
  selectedService: CASBServiceNameEnum
}

export interface CASBFilesAPIParams {
  search?: string
  after?: string
  createdBy?: string
  order?: 'asc' | 'desc'
  classifications?: CASBClassificationOptionsType[]
  shareStatus?: ShareStatus[]
  isReviewed?: boolean
  hasPersonalEmailShare?: boolean
  sharedWithDomains?: string[]
  notSharedWithDomains?: string[]
}

export type CASBFilesAPIFiltersPayload = Omit<
  CASBFilesAPIParams,
  | 'classifications'
  | 'shareStatus'
  | 'sharedWithDomains'
  | 'notSharedWithDomains'
> & {
  classifications?: string
  shareStatus?: string
  sharedWithDomains?: string
  notSharedWithDomains?: string
}

export interface CASBFilesLiveInfoRequest {
  platform: string
  fileId: string
}

export interface CASBFilesLiveInfoResponse {
  appName: string
  fileSummary: {
    uniqueId: string
    shareStatus: ShareStatus
    sharedWith: string[]
    fileID: string
    unshareError?: string
    shareDetails: ShareDetails
  }
}

export interface CASBFileLiveInfo {
  data: CASBFilesLiveInfoResponse
}

export interface UnshareData extends UnshareFilesReqData {
  timestamp: number
}

export interface SearchValueFilters {
  search?: string
  classifications?: string[]
  shareStatus?: string[]
  createdBy?: string
  sharedWithDomains?: string[]
  notSharedWithDomains?: string[]
  hasPersonalEmailShare?: boolean
}

export enum CASBExportStatusEnum {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type CASBExportStatus = `${CASBExportStatusEnum}`

export interface CASBExportStartRequest {
  platform: string
}

export interface CASBExportPollRequest {
  platform: string
}

export interface CASBExportStartResponse {
  status: CASBExportStatusEnum.PENDING
  createdAt: string
}

export interface CASBExportPollResponse {
  status: CASBExportStatus
  createdAt: string
  updatedAt?: string
  /** Pre-signed URL, present when status is "completed" */
  downloadUrl?: string
  /** Present when status is "failed" */
  errorMessage?: string
}

export enum RHSFooterButtonStates {
  UNSHARE = 'unshare',
  REVIEW = 'review',
  REOPEN = 'reopen',
  PRIVATIZE = 'privatize',
}

export interface UnshareFilesReqData {
  appName: string
  uniqueIds?: string[]
  searchValueFilters?: SearchValueFilters
  unshareAll?: boolean
  shareStatusesToRemove?: ShareStatus[]
}

export interface ReviewFilesRequestData {
  appName: string
  uniqueIds?: string[]
  searchValueFilters?: SearchValueFilters
  isReviewed: boolean
  reviewComment?: string
  reviewAll?: boolean
}

export interface CASBFilesPageInfo {
  endCursor: string
  hasNextPage: boolean
}

export interface CASBFilesData {
  files: CASBFileResponse[]
  pageInfo: CASBFilesPageInfo
}

export interface CASBFilesResponse {
  data: CASBFilesData
}

export interface CASBOverviewData {
  data: CASBOverviewResponse
}

export interface LinkToState {
  shareStatus?: ShareStatusEnum
  classifications?: string
}

export interface LinkTo {
  pathname: string
  state: LinkToState
}

export enum ClassificationsFilter {
  IP = 'ip',
  PHI = 'phi',
  PCI = 'pci',
  PII = 'pii',
  OTHER = 'other',
  EXTERNAL = 'EXTERNAL',
}

/**
 * Chart Types
 */

export type ClassificationTypes = 'ip' | 'pii' | 'phi' | 'pci' | 'other'

export type ReviewedStatus = {
  [key in ClassificationTypes]?: boolean
}

export interface Weights {
  [key: string]: number
}

export interface OverviewDataValidationResult {
  hasError: boolean
  needsPublicAdjustment: boolean
  publicFiles: number
  classificationSum: number
}

export interface NamedClassificationBox {
  name: string
  link: string
  color: string
  fill: string
  fillOpacity?: string
  opacity: string
  showBorder: boolean
  fontSize: number
  fontFamily: string
  fontWeight: number
  lineHeight: number
  padding: number
  hover: {
    border: {
      width: number
      color: string
    }
    opacity: number
    fill: string
  }
}

export interface NamedBoxesClassification {
  [key: string]: NamedClassificationBox
}

export interface NamedBoxes {
  classifications: NamedBoxesClassification
  reviewedColor: string
}

export interface DimensionWidth {
  width: number
}

export interface DimensionHeight {
  height: number
}

export interface Dimension extends DimensionWidth, DimensionHeight {}

export interface NodesDimensions {
  [classification: string]: Dimension
}

export interface GradientStop {
  offset?: string
  color?: string
  opacity?: string
}

export interface LinearGradient {
  id: string
  x1: string
  y1: string
  x2: string
  y2: string
  stops: GradientStop[]
  type: string
}

export interface RadialGradient {
  id: string
  cx: number
  cy: number
  r: number
  stops: GradientStop[]
  type: string
}

export type MixGradient = LinearGradient | RadialGradient

export type OtherGradient = MixGradient[]

export type GradientType = MixGradient | OtherGradient

export type BaseGradient = {gradientUnits: string; gradientTransform: string}

export type AllGradientType = LinearGradient & RadialGradient & BaseGradient

export interface GradientsTypes {
  [key: string]: GradientType | GradientType[]
}

export interface Colors {
  [key: string]: string
}

export interface CASBRoutePathParam {
  service: string
}

// The below values are linked with their corresponding casb.json translations.
export enum MakePrivateTextsEnum {
  REMOVE_PUBLIC = 'REMOVE_PUBLIC',
  REMOVE_EXTERNAL = 'REMOVE_EXTERNAL',
  REMOVE_PUBLIC_EXTERNAL = 'REMOVE_PUBLIC_EXTERNAL',
}

export interface DomainFileShares {
  domain: string
  numFilesShared: number
}

export interface DomainsAndFileShares {
  numDomains: number
  numFilesShared: number
}

export interface PersonalShares {
  numFilesShared: number
}

export interface CorporateShares {
  topShares: DomainFileShares[]
  bottomShares: DomainsAndFileShares
}

export interface ShareStats {
  personalShares: PersonalShares
  corporateShares: CorporateShares
}

export interface CASBShareStatsData {
  appName: string
  shareStats: ShareStats
}

export interface CASBShareStatsResponse {
  data: CASBShareStatsData
}

export interface CASBShareStatsPlotData {
  data: CASBShareStatsPlot | undefined
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  error: unknown
}

export interface CASBShareStatsPlot {
  numFilesSharedPersonal: number
  top3CorporateShares: DomainFileShares[]
  bottomCorporateShares: DomainsAndFileShares
}

export interface AccessEmailsParams {
  emails?: string[]
  isExternal?: boolean
}

// File Location Detail interfaces
export interface CASBFileLocationDetail {
  uniqueId: string
  driveName: string
  driveOwner?: string // Owner of the drive (e.g., "John_OCallaghan")
  folderName: string
  filePath?: string // Microsoft only - matches backend response
  path?: string // Path field from API
  publicUrl?: string // Based on rules above
  driveType?: string // Accept any string for normalization
  fileName?: string // File name fallback
}

export interface CASBFileLocationDetailData {
  appName: string
  shareStatus: ShareStatus
  fileLocation: CASBFileLocationDetail
}

export interface CASBFileLocationDetailResponse {
  data: CASBFileLocationDetailData
}
